import type {
  ConnectionState,
  ControlAcknowledgement,
  SessionSnapshot,
  TimelineEntry,
  TimelineUpdate,
  GenerationControls
} from "@stimulantnoise/protocol";

export interface ClientSessionState {
  connection: ConnectionState;
  snapshot: SessionSnapshot | undefined;
  timeline: TimelineEntry[];
  last_revision: number;
  pending_controls: Record<string, { submitted_at: string; value: number | (Partial<GenerationControls> & { energy?: number }) }>;
  last_ack: ControlAcknowledgement | undefined;
  conflict: string | undefined;
}

export type SessionAction =
  | { type: "snapshot"; value: SessionSnapshot }
  | { type: "timeline"; value: TimelineUpdate }
  | { type: "ack"; value: ControlAcknowledgement }
  | { type: "connection"; value: ConnectionState }
  | { type: "control.pending"; event_id: string; value: number | (Partial<GenerationControls> & { energy?: number }); submitted_at: string }
  | { type: "conflict.clear" };

export const initialClientSessionState: ClientSessionState = {
  connection: "offline",
  snapshot: undefined,
  timeline: [],
  last_revision: -1,
  pending_controls: {},
  last_ack: undefined,
  conflict: undefined
};

function sortedUnique(entries: TimelineEntry[]): TimelineEntry[] {
  const bySequence = new Map<number, TimelineEntry>();
  for (const entry of entries) bySequence.set(entry.sequence, entry);
  return [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
}

function reconcileTimeline(
  current: TimelineEntry[],
  update: TimelineUpdate
): { timeline: TimelineEntry[]; conflict?: string } {
  if (update.replace_from_sequence === undefined) {
    return { timeline: sortedUnique([...current, ...update.entries]) };
  }

  const replacementBoundary = update.replace_from_sequence;
  const immutableConflict = current.find(
    (entry) => entry.sequence >= replacementBoundary && entry.state === "committed"
  );
  if (immutableConflict) {
    return {
      timeline: current,
      conflict: `revision ${update.timeline_revision} attempted to replace committed sequence ${immutableConflict.sequence}`
    };
  }
  return {
    timeline: sortedUnique([
      ...current.filter((entry) => entry.sequence < replacementBoundary),
      ...update.entries
    ])
  };
}

export function reduceSession(state: ClientSessionState, action: SessionAction): ClientSessionState {
  switch (action.type) {
    case "connection":
      return { ...state, connection: action.value };
    case "snapshot":
      return {
        ...state,
        snapshot: action.value,
        timeline: sortedUnique(action.value.entries),
        last_revision: action.value.timeline_revision,
        conflict: undefined
      };
    case "timeline": {
      if (action.value.timeline_revision <= state.last_revision) return state;
      const result = reconcileTimeline(state.timeline, action.value);
      if (result.conflict) return { ...state, conflict: result.conflict };
      return {
        ...state,
        timeline: result.timeline,
        last_revision: action.value.timeline_revision,
        snapshot: state.snapshot
          ? {
              ...state.snapshot,
              timeline_revision: action.value.timeline_revision,
              entries: result.timeline
            }
          : undefined
      };
    }
    case "control.pending":
      return {
        ...state,
        pending_controls: {
          ...state.pending_controls,
          [action.event_id]: { submitted_at: action.submitted_at, value: action.value }
        }
      };
    case "ack": {
      const pending = { ...state.pending_controls };
      delete pending[action.value.event_id];
      return {
        ...state,
        pending_controls: pending,
        last_ack: action.value,
        snapshot: state.snapshot
          ? {
              ...state.snapshot,
              control_epoch: action.value.control_epoch,
              energy_target: action.value.effective_target,
              ...(action.value.effective_controls ? { generation_controls: action.value.effective_controls } : {}),
              ...(action.value.effective_from_ms !== undefined ? { controls_effective_from_ms: action.value.effective_from_ms } : {}),
              ...(action.value.effective_from_sequence !== undefined ? { controls_effective_from_sequence: action.value.effective_from_sequence } : {})
            }
          : undefined
      };
    }
    case "conflict.clear":
      return { ...state, conflict: undefined };
  }
}

export function clampEnergy(value: number): number {
  return Math.min(1, Math.max(0, Math.round(value * 1000) / 1000));
}
